import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  stripSearchParams,
  useRouteContext,
} from "@tanstack/react-router";
import { useCompanyCard } from "@/client/components/company-card";
import { PassiveSlot } from "@/client/components/lazy-slot";
import { NavTree } from "@/client/components/nav-tree";
import { DashboardColumns, Page } from "@/client/components/page";
import {
  HeldButton,
  HeldTitle,
  PagePending,
} from "@/client/components/page-pending";
import { clientCopy } from "@/client/copy";
import { companyCardReads } from "@/client/lib/company-card-reads";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { holidaysQuery, scheduleQuery } from "@/client/lib/schedule-query";
import { missedCheckOutCard } from "@/client/lib/screen-parts";
import { ensureTenantData, useModuleOn } from "@/client/lib/session";
import { doneOn, todayLists } from "@/client/lib/task-days";
import { tasksQuery } from "@/client/lib/tasks-query";
import { useCompleteTask } from "@/client/lib/use-complete-task";
import { useNow } from "@/client/lib/use-now";
import { usePhone } from "@/client/lib/use-phone";
import {
  leaveOffQuery,
  missedCheckOutsQuery,
  myTodayQuery,
  todayQuery,
} from "@/features/attendance/attendance-query";
import { CheckInControl } from "@/features/attendance/components/check-in-control";
import { attendanceCopy } from "@/features/attendance/copy";
import { dayIn } from "@/features/attendance/rules";
import { invoicesQuery } from "@/features/invoices/invoices-query";
import { ReviewRequests } from "@/features/leave/components/review-requests";
import { waitingLeaveQuery } from "@/features/leave/leave-query";
import { profileQuery } from "@/features/settings/settings-query";
import {
  HEADLINE,
  HomeHeadline,
} from "@/features/tasks/components/home-headline";
import { TodayOnHome } from "@/features/tasks/components/today-on-home";
import {
  type ListedTask,
  TASKS_SEARCH_DEFAULTS,
  tasksSearch,
} from "@/features/tasks/schemas";
import { useMoveToToday } from "@/features/tasks/use-task-writes";

export const Route = createFileRoute("/_authenticated/$slug/")({
  // Done today open or closed, as on Tasks.
  validateSearch: tasksSearch,
  search: { middlewares: [stripSearchParams(TASKS_SEARCH_DEFAULTS)] },
  pendingMs: PENDING_MS,
  head: ({ match }) => ({
    meta: [{ title: pageTitle(match.context.companyName) }],
  }),
  loader: async ({ context }) => {
    const day = dayIn(context.companyTimezone, new Date());
    const on = context.companyModules;
    const decides = context.role === "admin" || context.role === "manager";
    // The morning question waits on nothing: Home opens without it, and it arrives after.
    if (on.includes("attendance")) {
      void context.queryClient
        .fetchQuery(missedCheckOutsQuery)
        .then((missed) =>
          missed.length > 0 ? missedCheckOutCard.load() : null,
        )
        .catch(() => undefined);
    }
    // The company card is an Admin's or a Manager's; an Employee's Home is the Today list alone.
    const reads = companyCardReads(context.role, {
      attendance: on.includes("attendance"),
      invoices: on.includes("invoices"),
    });
    await Promise.all([
      ensureTenantData(context.queryClient, tasksQuery),
      ensureTenantData(context.queryClient, profileQuery),
      on.includes("leave") &&
        decides &&
        ensureTenantData(context.queryClient, waitingLeaveQuery),
      reads.invoices && ensureTenantData(context.queryClient, invoicesQuery),
      on.includes("attendance") &&
        ensureTenantData(context.queryClient, myTodayQuery(day)),
      ...(reads.attendance
        ? [
            ensureTenantData(context.queryClient, todayQuery(day)),
            ensureTenantData(context.queryClient, scheduleQuery),
            ensureTenantData(
              context.queryClient,
              holidaysQuery(Number(day.slice(0, 4))),
            ),
            ensureTenantData(
              context.queryClient,
              leaveOffQuery(day, day, on.includes("leave")),
            ),
          ]
        : []),
    ]);
  },
  pendingComponent: HomePending,
  component: Home,
});

function Home() {
  const { slug } = Route.useParams();
  const { role, companyName, companyTimezone } = Route.useRouteContext();
  const attendance = useModuleOn("attendance");
  const leave = useModuleOn("leave");
  const invoices = useModuleOn("invoices");
  const decides = role === "admin" || role === "manager";
  const phone = usePhone();
  // Every minute, so the greeting and the lists turn over on the company's clock.
  const now = useNow(60_000);
  const day = dayIn(companyTimezone, now);
  const dayOf = (at: Date) => dayIn(companyTimezone, at);

  const { data: tasks } = useSuspenseQuery(tasksQuery);
  const { data: profile } = useSuspenseQuery(profileQuery);
  const me = profile.personId;
  const { complete, uncheck, kept, leaving } = useCompleteTask();
  const moveToToday = useMoveToToday();
  const { lists, late, due } = headCount(tasks, me, day, kept);
  // A row kept for its undo is still drawn in Today.
  const done = doneOn(tasks, me, day, dayOf).filter(
    (task) => !kept.has(task.id),
  );
  const reads = companyCardReads(role, { attendance, invoices });
  // From the modules, never from what the card holds, so the page keeps its shape all session.
  const side = reads.attendance || reads.invoices;
  const card = useCompanyCard({
    slug,
    companyName: companyName ?? "",
    day,
    now,
    timeZone: companyTimezone,
    leave,
    ...reads,
  });

  return (
    <Page shape={side ? "dashboard" : "list"} dated>
      <header>
        <HomeHeadline
          name={profile.name}
          timeZone={companyTimezone}
          due={due}
          late={late.length}
          now={now}
        />
      </header>

      {/* Hidden while neither the check-in control nor Review requests is drawn in it. */}
      <div className="flex flex-wrap items-center gap-2 [&:not(:has(button))]:hidden">
        {attendance && <CheckInControl timeZone={companyTimezone} />}
        {leave && decides && <ReviewRequests slug={slug} />}
      </div>

      <DashboardColumns side={side ? (card ?? false) : null} held>
        <TodayOnHome
          today={day}
          tasks={tasks}
          lists={lists}
          done={done}
          kept={kept}
          dayOf={dayOf}
          leaving={leaving}
          onComplete={complete}
          onReopen={uncheck}
          onMoveToToday={late.length > 0 ? () => moveToToday(late, day) : null}
        />
      </DashboardColumns>

      {/* Home is the phone's hub: the modules, projects and Settings the rail holds on a desktop. */}
      {phone && (
        <nav aria-label={clientCopy.nav.label} className="flex flex-col gap-3">
          <NavTree slug={slug} hub />
        </nav>
      )}
      {attendance && <MissedCheckOut timeZone={companyTimezone} />}
    </Page>
  );
}

/** Rows kept checked while their undo is offered are done, so the headline no longer counts them. */
function headCount(
  tasks: readonly ListedTask[],
  me: string,
  day: string,
  kept?: ReadonlyMap<string, ListedTask>,
) {
  const lists = todayLists(tasks, me, day, kept);
  const late = lists.overdue.filter((task) => task.status !== "done");
  const due =
    late.length + lists.today.filter((task) => task.status !== "done").length;
  return { lists, late, due };
}

/**
 * The greeting from the working set when it holds the tasks and the profile, else held at its two
 * lines. The company card is likely wherever one can show, so the page pends as a Dashboard there.
 */
function HomePending() {
  const { role, companyTimezone, companyModules } = useRouteContext({
    from: "/_authenticated/$slug",
  });
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const tasks = queryClient.getQueryData(tasksQuery.queryKey);
  const profile = queryClient.getQueryData(profileQuery.queryKey);
  const attendance = companyModules.includes("attendance");
  const leave = companyModules.includes("leave");
  const decides = role === "admin" || role === "manager";
  const reads = companyCardReads(role, {
    attendance,
    invoices: companyModules.includes("invoices"),
  });
  const card = reads.attendance || reads.invoices;
  const now = new Date();
  const day = dayIn(companyTimezone, now);
  const counted =
    tasks && profile ? headCount(tasks, profile.personId, day) : null;
  return (
    <PagePending
      shape={card ? "dashboard" : "list"}
      dated
      header={
        <>
          <header>
            {profile && counted ? (
              <HomeHeadline
                name={profile.name}
                timeZone={companyTimezone}
                due={counted.due}
                late={counted.late.length}
                now={now}
              />
            ) : (
              <HeldTitle className={HEADLINE} lines={["w-72", "w-96"]} />
            )}
          </header>
          {/*
           * As Home draws it: the check-in real once its day was read, else held where and as
           * wide as it lands; Review requests only once read, as most days nothing waits. Hidden,
           * as Home's is, while it holds neither a control nor a held one.
           */}
          {attendance || (leave && decides) ? (
            <div className="flex flex-wrap items-center gap-2 [&:not(:has(button)):not(:has([aria-hidden]))]:hidden">
              {!attendance ? null : queryClient.getQueryData(
                  myTodayQuery(day).queryKey,
                ) ? (
                <CheckInControl timeZone={companyTimezone} />
              ) : (
                // Unread, as it draws once someone is in: Home is opened mostly in the working day.
                <div className="flex flex-wrap items-center gap-3.5">
                  <HeldTitle className="text-body" lines={["w-56"]} />
                  <HeldButton>{attendanceCopy.today.checkOut}</HeldButton>
                </div>
              )}
              {leave &&
              decides &&
              queryClient.getQueryData(waitingLeaveQuery.queryKey) ? (
                <ReviewRequests slug={slug} />
              ) : null}
            </div>
          ) : null}
        </>
      }
    />
  );
}

function MissedCheckOut({ timeZone }: { readonly timeZone: string }) {
  const { data: missed = [] } = useQuery(missedCheckOutsQuery);
  return missed.length > 0 ? (
    <PassiveSlot of={missedCheckOutCard} props={{ timeZone }} />
  ) : null;
}
